import React, { useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';

import { PREVENTIVE_TASK_ASSIGNEE_PATH } from '../../../../config/paths';

import {
  clearPreventiveTaskAssignee,
  getPreventiveTaskAssignee,
  putPreventiveTaskAssignee,
} from '../../../../actions/task-schedule/PreventiveTaskAssignee';

import {
  breadcrumbsPropTypes,
  matchPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';

import Content from '../../../layout/Content';
import Loader from '../../../common/Loader';
import NoDataResource from '../../../common/resource/NoDataResource';
import PreventiveTaskAssigneeForm from './PreventiveTaskAssigneeForm';

import { PREVENTIVE_TASK_ASSIGNEE_SCOPE } from '../../../../config/constants';

const getSelectValue = (field) => {
  if (field && typeof field === 'object') return field.value;
  return field;
};

const getSelectLabel = (field) => {
  if (field && typeof field === 'object') return field.label;
  return null;
};

const buildPreventiveTaskAssigneeBody = (formValues) => {
  const assignmentScope = getSelectValue(formValues.assignmentScope);

  const departmentId = getSelectValue(formValues.departmentId);
  const departmentName = getSelectLabel(formValues.departmentId);

  const areaId = getSelectValue(formValues.areaId);
  const areaName = getSelectLabel(formValues.areaId);

  const positionId = getSelectValue(formValues.positionId);
  const positionName = getSelectLabel(formValues.positionId);

  const userId = getSelectValue(formValues.userId);

  return {
    preventiveTaskId: getSelectValue(formValues.preventiveTaskId),

    assignmentScope,

    departamentId: departmentId,
    departamentName: departmentName,

    areaId,
    areaName,

    positionId:
      assignmentScope === PREVENTIVE_TASK_ASSIGNEE_SCOPE.ROLE.value
        ? positionId
        : null,

    positionName:
      assignmentScope === PREVENTIVE_TASK_ASSIGNEE_SCOPE.ROLE.value
        ? positionName
        : null,

    userId:
      assignmentScope === PREVENTIVE_TASK_ASSIGNEE_SCOPE.PERSON.value
        ? userId
        : null,

    active: true,
  };
};

const generateInitialValues = ({
  id,
  preventiveTaskId,
  preventiveTask,
  assignmentScope,
  departamentId,
  departamentName,
  areaId,
  areaName,
  positionId,
  positionName,
  userId,
  user,
  active,
}) => ({
  id,

  preventiveTaskId: preventiveTask
    ? {
        value: preventiveTask.id,
        label: preventiveTask.companyPreventiveTaskId
          ? `${preventiveTask.companyPreventiveTaskId} - ${preventiveTask.name}`
          : preventiveTask.name,
      }
    : {
        value: preventiveTaskId,
        label: preventiveTaskId,
      },

  assignmentScope: assignmentScope
    ? PREVENTIVE_TASK_ASSIGNEE_SCOPE[assignmentScope]
    : null,

  departmentId: departamentId
    ? {
        value: departamentId,
        label: departamentName || departamentId,
      }
    : null,

  areaId: areaId
    ? {
        value: areaId,
        label: areaName || areaId,
      }
    : null,

  positionId: positionId
    ? {
        value: positionId,
        label: positionName || positionId,
      }
    : null,

  userId: userId
    ? {
        value: userId,
        label: user.fullName,
      }
    : null,

  active: {
    value: active,
    label: active ? 'Activo' : 'Inactivo',
  },
});

const EditPreventiveTaskAssignee = ({
  breadcrumbs,
  dispatchGetPreventiveTaskAssignee,
  dispatchClearPreventiveTaskAssignee,
  dispatchPutPreventiveTaskAssignee,
  match: {
    params: { id: preventiveTaskAssigneeId },
  },
  loading,
  preventiveTaskAssignee,
}) => {
  useLayoutEffect(() => {
    dispatchGetPreventiveTaskAssignee({ preventiveTaskAssigneeId });

    return () => dispatchClearPreventiveTaskAssignee();
  }, []);

  const onSubmit = (formValues) => {
    const body = buildPreventiveTaskAssigneeBody(formValues);

    dispatchPutPreventiveTaskAssignee(preventiveTaskAssigneeId, body);
  };

  let content;

  if (loading) content = <Loader />;
  else if (preventiveTaskAssignee.isEmpty())
    content = <NoDataResource returnPage={PREVENTIVE_TASK_ASSIGNEE_PATH} />;
  else
    content = (
      <PreventiveTaskAssigneeForm
        onSubmit={onSubmit}
        initialValues={generateInitialValues(preventiveTaskAssignee.toJS())}
        loading={loading}
      />
    );

  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Editar Asignación de Tarea Programada"
      subtitle="Editar asignación de tarea programada"
      content={content}
    />
  );
};

EditPreventiveTaskAssignee.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchClearPreventiveTaskAssignee: PropTypes.func.isRequired,
  dispatchGetPreventiveTaskAssignee: PropTypes.func.isRequired,
  dispatchPutPreventiveTaskAssignee: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  match: matchPropTypes.isRequired,
  preventiveTaskAssignee: PropTypes.instanceOf(Immutable.Map).isRequired,
};

EditPreventiveTaskAssignee.defaultProps = {
  loading: false,
};

const mapDispatchToProps = {
  dispatchClearPreventiveTaskAssignee: clearPreventiveTaskAssignee,
  dispatchGetPreventiveTaskAssignee: getPreventiveTaskAssignee,
  dispatchPutPreventiveTaskAssignee: putPreventiveTaskAssignee,
};

const mapStateToProps = ({ TaskScheduleUnit }) => ({
  breadcrumbs: [
    ...TaskScheduleUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Lista de Asignaciones Tareas Programadas',
      href: PREVENTIVE_TASK_ASSIGNEE_PATH,
    },
    {
      text: 'Editar',
      href: '',
    },
  ],
  preventiveTaskAssignee: TaskScheduleUnit.PreventiveTaskAssignee.getIn([
    'current',
    'content',
  ]),
  loading: !TaskScheduleUnit.PreventiveTaskAssignee.getIn([
    'current',
    'activity',
  ]).isEmpty(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditPreventiveTaskAssignee);
